/**
 * Prepress home's artwork steps, each in a dialog (docs/features/
 * order-to-invoice/66_views_and_frontend_artwork_update_spec.md §4):
 * - Resolve an orphan: a file nobody could place goes on a version's part.
 *   The number it was found with is the start; the versions it can go on come
 *   in the read.
 * - Discard an orphan: its bytes are deleted, with why.
 * - Decide a held check: say what an unrecognised check means (can't print,
 *   worth knowing, we'll sort it); the version carries on once every one is
 *   decided.
 *
 * `onSubmit` resolves to the refusal to show, or null once done.
 */
import { useId, useState } from "react";
import type {
  PrepressHeldFinding,
  PrepressOrphan,
} from "@shared-core/data/artwork-staff-types";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Textarea } from "../../primitives/textarea";
import { FailureBanner, Field } from "../orders/OrderDialogs";
import type { OrderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";

type Submit<T> = (input: T) => Promise<OrderStepFailure | null>;

const SELECT_CLASS =
  "h-9 w-full rounded-md border border-border bg-card px-3 text-sm";

function Buttons({
  busy,
  label,
  onClose,
}: {
  busy: boolean;
  label: string;
  onClose: () => void;
}) {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" disabled={busy}>
        {label}
      </Button>
    </DialogFooter>
  );
}

// ── Resolve an orphan ────────────────────────────────────────────────────

export function ResolveOrphanDialog({
  orphan,
  busy,
  onClose,
  onSubmit,
}: {
  orphan: PrepressOrphan;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit<{ owner_version_id: string; part_key: string }>;
}) {
  const id = useId();
  const [versionId, setVersionId] = useState(
    orphan.resolve_targets.length === 1
      ? orphan.resolve_targets[0]!.owner_version_id
      : "",
  );
  const target = orphan.resolve_targets.find(
    (t) => t.owner_version_id === versionId,
  );
  const [partKey, setPartKey] = useState(
    target?.parts.length === 1 ? target.parts[0]!.part_key : "",
  );
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const versionProblem = versionId ? undefined : "Choose the version.";
  const partProblem = partKey ? undefined : "Choose the part.";
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Place {orphan.original_filename}</DialogTitle>
          <DialogDescription>
            {orphan.matched_number
              ? `Found with ${orphan.matched_number}. Choose where it goes: it is checked as it arrives, as if the customer had uploaded it.`
              : "Choose where it goes: it is checked as it arrives."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            if (versionProblem || partProblem) return;
            const refused = await onSubmit({
              owner_version_id: versionId,
              part_key: partKey,
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field
            id={`${id}-version`}
            label="Version"
            error={touched ? versionProblem : undefined}
          >
            {(aria) => (
              <select
                {...aria}
                className={SELECT_CLASS}
                value={versionId}
                onChange={(e) => {
                  setVersionId(e.target.value);
                  const next = orphan.resolve_targets.find(
                    (t) => t.owner_version_id === e.target.value,
                  );
                  setPartKey(
                    next?.parts.length === 1 ? next.parts[0]!.part_key : "",
                  );
                }}
              >
                <option value="">Choose a version</option>
                {orphan.resolve_targets.map((t) => (
                  <option key={t.owner_version_id} value={t.owner_version_id}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
          {target && target.parts.length > 1 && (
            <Field
              id={`${id}-part`}
              label="Part"
              error={touched ? partProblem : undefined}
            >
              {(aria) => (
                <select
                  {...aria}
                  className={SELECT_CLASS}
                  value={partKey}
                  onChange={(e) => setPartKey(e.target.value)}
                >
                  <option value="">Choose a part</option>
                  {target.parts.map((p) => (
                    <option key={p.part_key} value={p.part_key}>
                      {p.label ?? p.part_key}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}
          <Buttons busy={busy} label="Place file" onClose={onClose} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Discard an orphan ────────────────────────────────────────────────────

export function DiscardOrphanDialog({
  orphan,
  busy,
  onClose,
  onSubmit,
}: {
  orphan: PrepressOrphan;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit<{ reason: string }>;
}) {
  const id = useId();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const problem = reason.trim() ? undefined : "Say why it is discarded.";
  const error = (touched ? problem : undefined) ?? failure?.fieldErrors.reason;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Discard {orphan.original_filename}</DialogTitle>
          <DialogDescription>
            The file is deleted. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            if (problem) return;
            const refused = await onSubmit({ reason: reason.trim() });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field id={`${id}-reason`} label="Why" error={error}>
            {(aria) => (
              <Textarea
                {...aria}
                rows={2}
                value={reason}
                placeholder="A duplicate of a file already placed"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <Buttons busy={busy} label="Discard file" onClose={onClose} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Decide a held check ──────────────────────────────────────────────────

const TIERS = [
  {
    tier: "cant_print",
    label: "Can't print",
    help: "The customer is asked for a new file.",
  },
  {
    tier: "worth_knowing",
    label: "Worth knowing",
    help: "We can print it; the customer ticks the point when they approve the proof.",
  },
  {
    tier: "we_will_sort",
    label: "We'll sort it",
    help: "Our correction deals with it; the customer is told it is done.",
  },
] as const;

export function DecideHeldFindingDialog({
  finding,
  busy,
  onClose,
  onSubmit,
}: {
  finding: PrepressHeldFinding;
  busy: boolean;
  onClose: () => void;
  onSubmit: Submit<{ tier: string }>;
}) {
  const id = useId();
  const [tier, setTier] = useState("");
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const problem = tier ? undefined : "Choose what this check means.";
  const error = (touched ? problem : undefined) ?? failure?.fieldErrors.tier;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{finding.check_name}</DialogTitle>
          <DialogDescription>
            {finding.subject}
            {finding.detail && ` · ${finding.detail}`}. The customer sees only
            that we are taking a closer look until this is decided.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setTouched(true);
            if (problem) return;
            const refused = await onSubmit({ tier });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <fieldset
            className="space-y-2"
            aria-describedby={error ? `${id}-error` : undefined}
          >
            <legend className="text-sm font-medium">What it means</legend>
            {TIERS.map((t) => (
              <label key={t.tier} className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name={`${id}-tier`}
                  className="mt-1"
                  value={t.tier}
                  checked={tier === t.tier}
                  onChange={() => setTier(t.tier)}
                />
                <span>
                  {t.label}
                  <span className="block text-xs text-muted-foreground">
                    {t.help}
                  </span>
                </span>
              </label>
            ))}
            {error && (
              <p id={`${id}-error`} className="text-sm text-red-600">
                {error}
              </p>
            )}
          </fieldset>
          <Buttons busy={busy} label="Decide" onClose={onClose} />
        </form>
      </DialogContent>
    </Dialog>
  );
}
